import React from "react";
import {
  Calculator,
  Calendar,
  ChevronsLeft,
  ChevronsRight,
  History,
  IdCard,
  ListChecks,
  Receipt,
  StickyNote,
  User,
} from "lucide-react";

/**
 * The original right-side tools region, as shared, fixture-free
 * presentation (WORKSPACE-FEATURE-001 S14; authoritative original
 * `WorkspaceAgentToolsCard` at commit 23d500e27; reference
 * `docs/design/prototypes/workspace-v1-reference.png`).
 *
 * AW-VIS-3 SEPARATED "SELECTABLE" FROM "HAS A CAPABILITY"
 * -------------------------------------------------------
 * AW-VIS-2 restored this geometry with a single `availability` flag that
 * conflated two different questions, and answered both `false`: can the
 * agent select this tool, and does its panel do anything real? The result
 * was a rail that looked right and could not be operated at all.
 *
 * Those questions are now separate, because their answers genuinely
 * differ. Selecting a tool and opening its panel is a presentation act
 * with no data dependency -- the original shell has always done it with
 * two `useState` hooks and nothing else. Whether the opened panel can
 * show a lead's notes is a backend question, and for seven of the eight
 * tools the answer is still no.
 *
 *   `selectable[key]`  -- may the agent choose this tool? (AW-VIS-3: all
 *                         eight, because choosing is free)
 *   `panelContent`     -- what the chosen tool actually renders, decided
 *                         entirely by the caller
 *
 * So the rail no longer decides what a tool can do. It decides only
 * whether a tool can be picked, and renders whatever the caller hands
 * back for the picked one.
 *
 * THE SAFETY RULE THAT DID NOT CHANGE
 * -----------------------------------
 * A tool that is not selectable still binds NO handler at all --
 * `disabled`, `aria-disabled`, `tabIndex={-1}`, and `onClick` literally
 * `undefined`, so a caller passing `onSelectTool` by mistake cannot make
 * it live. That is still enforced here rather than at the call site.
 *
 * What did change is `tabIndex` for a *selectable* tool. Under AW-VIS-2
 * every tool was disabled and removing all eight from the tab order was
 * correct. Now that selection is a supported action, keeping them out of
 * the tab order would make the rail undiscoverable by keyboard, which
 * S14.3 forbids. A selectable tool is a real, focusable, Enter/Space
 * activatable button with `aria-pressed` reflecting the active tool.
 *
 * SELECTING A TOOL IS NOT A CAPABILITY CLAIM
 * ------------------------------------------
 * Seven of the eight panels open onto honest deferred copy. That is the
 * point, not a shortcoming: the agent learns what each tool will be and
 * that it is not here yet, instead of meeting eight dead chips. No panel
 * fabricates data, and no selection issues a request -- this file has no
 * network import and no effect.
 *
 * NO AI AFFORDANCE, AND THIS IS NOT THE SAME RULE
 * -----------------------------------------------
 * The committed reference mockup shows an "AI Insights" tool with a
 * "New" badge. It is absent here and must stay absent. S14.2 and S12.12
 * prohibit an AI tool, icon, placeholder *or disabled panel* outright --
 * that is a prohibition on the capability, not a deferral pending a
 * contract, so neither the scaffolding policy nor selectability reaches
 * it. The eight tools below are exactly the authoritative set from
 * 23d500e27.
 */

export const WORKSPACE_TOOL = Object.freeze({
  LEAD: "lead",
  DISCOVERY: "discovery",
  QUOTE: "quote",
  CALCULATOR: "calculator",
  CALENDAR: "calendar",
  ACTIONS: "actions",
  NOTES: "notes",
  HISTORY: "history",
});

/**
 * Authoritative order, from the original rail. Stable order is itself a
 * requirement (S14.3): Lead and Discovery first because agents open
 * them most while reading the Script.
 */
export const WORKSPACE_TOOL_CATALOG = Object.freeze([
  { key: WORKSPACE_TOOL.LEAD, label: "Lead", Icon: IdCard },
  { key: WORKSPACE_TOOL.DISCOVERY, label: "Discovery", Icon: User },
  { key: WORKSPACE_TOOL.QUOTE, label: "Quote", Icon: Receipt },
  { key: WORKSPACE_TOOL.CALCULATOR, label: "Calculator", Icon: Calculator },
  { key: WORKSPACE_TOOL.CALENDAR, label: "Calendar", Icon: Calendar },
  { key: WORKSPACE_TOOL.ACTIONS, label: "Actions", Icon: ListChecks },
  { key: WORKSPACE_TOOL.NOTES, label: "Notes", Icon: StickyNote },
  { key: WORKSPACE_TOOL.HISTORY, label: "History", Icon: History },
]);

// Matches the established icon-chip treatment from the original rail and
// the Script Runner Quick Reference rail.
const SELECTED_ICON_CLASS = "bg-indigo-500/90 text-white hover:bg-indigo-500";
const IDLE_ICON_CLASS = "bg-white/10 text-slate-100 hover:bg-white/15";
const UNAVAILABLE_ICON_CLASS = "bg-white/[0.04] text-slate-500";

export default function WorkspaceToolRail({
  tools = WORKSPACE_TOOL_CATALOG,
  selectable = {},
  activeTool = null,
  onSelectTool = null,
  isExpanded = true,
  onToggleExpanded = null,
  panelHeading = null,
  panelContent = null,
  unavailableReason,
}) {
  const reasonId = "workspace-tool-rail-reason";
  const activeItem = tools.find((tool) => tool.key === activeTool) || null;
  const toggleAvailable = typeof onToggleExpanded === "function";

  return (
    <aside
      aria-label="Supporting tools"
      className="glass flex min-h-0 shrink-0 overflow-hidden rounded-xl"
      data-testid="workspace-context-dock"
      data-expanded={String(Boolean(isExpanded))}
    >
      {isExpanded ? (
        <div
          className="flex w-72 min-w-0 flex-col overflow-hidden"
          data-testid="workspace-tool-panel-region"
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-slate-100">
              {panelHeading || (activeItem ? activeItem.label : "Agent Tools")}
            </p>
          </div>
          <div
            className="min-h-0 flex-1 overflow-y-auto p-3"
            data-testid="workspace-tool-panel"
          >
            {panelContent || (
              <p className="text-xs leading-snug text-slate-500">{unavailableReason}</p>
            )}
          </div>
        </div>
      ) : null}

      <div
        className={`flex min-h-0 w-[72px] shrink-0 flex-col items-center px-2 py-3 ${
          isExpanded ? "border-l border-white/10" : ""
        }`}
        data-testid="workspace-tool-rail"
      >
        <button
          type="button"
          disabled={!toggleAvailable}
          aria-disabled={!toggleAvailable || undefined}
          tabIndex={toggleAvailable ? undefined : -1}
          onClick={toggleAvailable ? onToggleExpanded : undefined}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse Agent Tools" : "Expand Agent Tools"}
          data-testid="workspace-tool-rail-toggle"
          data-selectable={String(toggleAvailable)}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
            toggleAvailable ? IDLE_ICON_CLASS : UNAVAILABLE_ICON_CLASS
          }`}
        >
          {isExpanded ? (
            <ChevronsRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        <div className="mt-3 h-px w-7 shrink-0 bg-white/10" aria-hidden="true" />

        {/* Tool icons scroll independently of the collapse/expand toggle
            above, so every tool remains reachable when the available height
            shrinks -- the toggle stays fixed; only this list scrolls. */}
        <div
          className="mt-3 flex min-h-0 w-full flex-1 flex-col items-center gap-2.5 overflow-y-auto"
          role="group"
          aria-label="Agent tools"
        >
          {tools.map((tool) => {
            const isSelectable =
              Boolean(selectable[tool.key]) && typeof onSelectTool === "function";
            const selected = isSelectable && activeTool === tool.key;
            return (
              <button
                key={tool.key}
                type="button"
                disabled={!isSelectable}
                aria-disabled={!isSelectable || undefined}
                aria-pressed={isSelectable ? selected : undefined}
                aria-describedby={isSelectable ? undefined : reasonId}
                tabIndex={isSelectable ? undefined : -1}
                onClick={isSelectable ? () => onSelectTool(tool.key) : undefined}
                data-testid={`workspace-tool-${tool.key}`}
                data-selectable={String(isSelectable)}
                data-active={String(selected)}
                className={`flex flex-col items-center gap-1 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                  isSelectable
                    ? "text-slate-400 hover:text-slate-100"
                    : "cursor-not-allowed text-slate-600"
                }`}
              >
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
                    selected
                      ? SELECTED_ICON_CLASS
                      : isSelectable
                        ? IDLE_ICON_CLASS
                        : UNAVAILABLE_ICON_CLASS
                  }`}
                >
                  <tool.Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-[10px] leading-none">{tool.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <p id={reasonId} className="sr-only">
        {unavailableReason}
      </p>
    </aside>
  );
}
